import { useMemo } from 'react';

import { useIsFocused } from '@react-navigation/native';

import { useMarketCacheStore } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { useInfiniteEventsWithMarkets } from '@/feature/league-hub-sbk/hooks/use-events-with-markets';
import { useSubscribeLeagueMatchUpdate } from '@/feature/league-hub-sbk/hooks/use-subscribe-league-match-update';
import { useSubscribeLeaguePopularMarketUpdate } from '@/feature/league-hub-sbk/hooks/use-subscribe-league-popular-market-update';
import { useSubscribeLeaguePopularOddsUpdate } from '@/feature/league-hub-sbk/hooks/use-subscribe-league-popular-odds-update';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useShallow } from 'zustand/react/shallow';

export const EVENT_LIST_PAGE_SIZE = 10;

type UseLeagueTraditionalMatchupDataWithMarketsProps = {
    leagueId: string;
    limit?: number;
    isLive?: boolean;
};

// Hook to fetch a list of events for a league using the traditional matchup card layout
export const useLeagueTraditionalMatchupDataWithMarkets = ({
    leagueId,
    limit = 5,
    isLive,
}: UseLeagueTraditionalMatchupDataWithMarketsProps) => {
    const isFocused = useIsFocused();
    const { data, isLoading, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteEventsWithMarkets(
        { leagueId, limit, isLive },
        { enabled: isFocused }
    );

    const allEvents = useMemo(() => data?.pages.flatMap(page => page.data) ?? [], [data]);

    // List of event ids that have published markets
    const activeEvents = useMarketCacheStore(
        useShallow(state => {
            return (
                allEvents
                    // filter out events that have published markets
                    .filter(event => {
                        return event.popular_markets.some(category =>
                            category.markets.some(market => state.markets[market.id]?.published ?? market.published)
                        );
                    })
                    // filter out events that are not live if isLive is true
                    .filter(event => (isLive ? event.status === 'LIVE' : true))
            );
        })
    );

    useSubscribeLeaguePopularOddsUpdate(leagueId, isFocused);
    useSubscribeLeaguePopularMarketUpdate(leagueId, isFocused);
    useSubscribeLeagueMatchUpdate(leagueId);
    useFetchOnResume(refetch);

    return {
        refetch,
        events: activeEvents,
        isLoading,
        hasAnyAvailableMarkets: activeEvents.length > 0,
        totalEventCount: allEvents.length,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
    };
};

export type LeagueMatchupData = ReturnType<typeof useLeagueTraditionalMatchupDataWithMarkets>;
